import { useState } from "react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "./ui/dialog";
import {
  BookOpen,
  ChevronDown,
  ChevronUp,
  User,
  Upload,
  Brain,
  Calendar,
  CheckCircle,
  FileText,
  AlertTriangle,
  Award,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Guide content — keeps text + screenshot together for each step.
// Screenshots live in /app/frontend/public/manual/.
// ---------------------------------------------------------------------------
const GUIDE_SECTIONS = [
  {
    id: "getting-started",
    title: "1. Getting Started",
    icon: User,
    intro:
      "After you are invited (or accepted via self-registration), you log in with the temporary password emailed to you. On first login the system forces you to set your own password.",
    steps: [
      "Open the invitation email and click the secure login link.",
      "Sign in with your email + temporary password (default: User@123).",
      "Set a new password when prompted.",
      "You arrive at your Candidate Dashboard — your single home for the whole assessment.",
    ],
    shot: {
      src: "/manual/public-login.png",
      alt: "Credentia AI sign-in page",
      caption:
        "The sign-in page. Use the email/password sent in your invitation. Forgot password? Use 'Forgot password' to receive a reset link.",
    },
  },
  {
    id: "dashboard-tour",
    title: "2. Your Dashboard at a Glance",
    icon: BookOpen,
    intro:
      "Your dashboard is the only place you need. It tracks every stage of your RPL journey from profile to award.",
    tips: [
      "Progress timeline — shows where you are: Profile → Documents → AI → Interview → IQA → Approved.",
      "Communications card — any request from your assessor or IQA shows here (info requests, written-interview links, appeal decisions).",
      "Documents card — upload new evidence at any stage when asked.",
      "Final award card — once IQA approves you, this card unlocks your credential PDF, QR code, and LinkedIn share buttons.",
    ],
    shot: {
      src: "/manual/candidate-dashboard.png",
      alt: "Candidate dashboard with progress timeline",
      caption:
        "Your dashboard. The progress bar at the top is the source of truth — green stages are complete, blue is current, grey is upcoming.",
    },
  },
  {
    id: "profile",
    title: "3. Complete Your 5-Section Profile",
    icon: FileText,
    intro:
      "Before you can upload any documents, all five profile sections must be saved. This information is read by the AI alongside your evidence.",
    steps: [
      "Personal — Full name, phone, nationality, date of birth, address.",
      "Employment — Current employer, role, years of experience, responsibilities.",
      "Training — Every training course, certification, qualification with issuing body and date.",
      "Work History — Previous positions: employer, role, dates, key duties.",
      "Declaration — Read and accept the authenticity declaration to lock the profile.",
    ],
    tips: [
      "The more detail you provide, the stronger your AI assessment will be.",
      "You can edit any section until the Declaration is signed.",
    ],
  },
  {
    id: "documents",
    title: "4. Upload Your Evidence",
    icon: Upload,
    intro:
      "After your profile is locked, the upload area unlocks. Add every piece of evidence that proves your skills.",
    steps: [
      "Use the Documents card on the dashboard.",
      "Drag-drop or click to add: certificates, employment references, project portfolios, training records, transcripts.",
      "Supported formats: PDF, DOC, DOCX, JPG, PNG, TXT (max 25 MB each).",
      "You can delete a document (trash icon) until AI analysis has been run.",
    ],
    tips: [
      "Text-readable PDFs work far better than photos of paper certificates.",
      "Name your files clearly (e.g. 'Reference_ABC-Co_2022.pdf').",
      "Upload everything relevant — more evidence = stronger assessment.",
    ],
  },
  {
    id: "ai-analysis",
    title: "5. AI Analysis & Requests for More Info",
    icon: Brain,
    intro:
      "The assessment team runs an AI analysis on your evidence. It maps your documents against the competency framework and produces a coverage report.",
    steps: [
      "AI runs automatically once your assessor triggers it.",
      "If gaps are found, you receive an email and the dashboard shows a 'Request for More Info' card.",
      "Upload the requested documents in your Documents card.",
      "Reply to the request from the Communications card so the assessor can re-run analysis.",
    ],
    warning:
      "You can be asked for additional information up to 3 times. After that, the case moves to scoring with the evidence on file.",
  },
  {
    id: "interview",
    title: "6. Your Interview (Live or Written)",
    icon: Calendar,
    intro:
      "Your assessor will choose one of two interview modes. Both are conducted entirely through this platform.",
    steps: [
      "Live Interview — You receive an email with 3 time slots. Pick one; you'll get a video meeting link to join at the chosen time.",
      "Written Interview — You receive a secure link to a 10-question form. No login required. Answer all questions before the deadline.",
      "The assessor may send up to 3 clarification questions. Reply via the same secure link.",
      "Score is recorded as Pass or Fail by the industry specialist.",
    ],
    tips: [
      "Reference real projects, certificates, and dates in every answer — generic answers score lower.",
      "If the assessor asks for clarification, reply within 48 hours to keep your case moving.",
    ],
  },
  {
    id: "iqa-and-appeals",
    title: "7. IQA Decision & Your Right to Appeal",
    icon: AlertTriangle,
    intro:
      "Independent Quality Assurance (IQA) reviews the full assessment before any award is issued. The IQA can approve, reject or send it back for more info.",
    steps: [
      "If approved → your case moves to Report Generation (next step).",
      "If rejected at assessor or IQA stage → you receive an email explaining why.",
      "You may submit an Appeal with a written statement and supporting evidence.",
      "An Appeal counter on your dashboard shows 'Appeal X of 3' — you have up to 3 appeals across the whole case.",
    ],
    warning:
      "After 3 appeals the case is closed and the decision becomes final.",
  },
  {
    id: "completion",
    title: "8. Final Award & Sharing Your Credential",
    icon: Award,
    intro:
      "Once IQA approves, your dashboard unlocks the credential card. This is your verified, sharable RPL outcome.",
    steps: [
      "Download your credential PDF (vector format, certificate-grade).",
      "Scan the printed QR code — anyone can verify the credential at /verify.",
      "Click 'Add to LinkedIn Profile' to add the qualification to your LinkedIn profile in one tap.",
      "Click 'Share to LinkedIn Feed' to publish a public announcement post.",
    ],
    tips: [
      "Digital Badges are awarded in 3 tiers: per-competency, professional-identity cluster, and framework-level (Distinction / Achievement / Partial).",
      "Your credential carries a unique Report ID — verifiers can confirm it instantly without you sending the certificate.",
    ],
  },
];

function Section({ section, expanded, onToggle }) {
  const Icon = section.icon;
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors text-left"
        data-testid={`guide-section-${section.id}`}
      >
        <Icon className="w-4 h-4 text-blue-600 flex-shrink-0" />
        <span className="font-medium text-sm text-slate-800 flex-1">
          {section.title}
        </span>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 space-y-3 text-sm text-slate-700">
          {section.intro && <p className="leading-relaxed">{section.intro}</p>}

          {section.steps && (
            <ol className="list-decimal list-outside ml-5 space-y-1.5">
              {section.steps.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ol>
          )}

          {section.tips && (
            <div className="rounded-md bg-blue-50 border border-blue-100 p-3">
              <p className="text-xs font-semibold text-blue-800 mb-1.5 uppercase tracking-wide">
                Tips
              </p>
              <ul className="list-disc list-outside ml-5 space-y-1 text-blue-900">
                {section.tips.map((t, i) => (
                  <li key={i}>{t}</li>
                ))}
              </ul>
            </div>
          )}

          {section.warning && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 flex gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-amber-900 text-sm">{section.warning}</p>
            </div>
          )}

          {section.shot && (
            <figure className="mt-2">
              <img
                src={section.shot.src}
                alt={section.shot.alt}
                loading="lazy"
                className="w-full border border-slate-200 rounded-md shadow-sm"
                data-testid={`guide-shot-${section.id}`}
              />
              <figcaption className="text-xs text-slate-500 mt-1.5 italic">
                {section.shot.caption}
              </figcaption>
            </figure>
          )}
        </div>
      )}
    </div>
  );
}

export default function CandidateGuide({ variant = "button" }) {
  const [open, setOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);

  const trigger =
    variant === "link" ? (
      <button
        className="text-sm text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1"
        data-testid="show-candidate-guide-btn"
      >
        <BookOpen className="w-4 h-4" />
        User Manual
      </button>
    ) : (
      <Button
        variant="outline"
        size="sm"
        className="text-blue-600 border-blue-200 hover:bg-blue-50"
        data-testid="show-candidate-guide-btn"
      >
        <BookOpen className="w-4 h-4 mr-1.5" />
        User Manual
      </Button>
    );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="candidate-guide-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-blue-800">
            <BookOpen className="w-5 h-5" />
            Candidate User Manual — Your RPL Journey, Step-by-Step
          </DialogTitle>
          <DialogDescription>
            Tap any section to expand. Screenshots show the exact screen you
            will see at that stage.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 pt-2">
          {GUIDE_SECTIONS.map((section) => (
            <Section
              key={section.id}
              section={section}
              expanded={expandedSection === section.id}
              onToggle={() =>
                setExpandedSection(
                  expandedSection === section.id ? null : section.id
                )
              }
            />
          ))}

          <div className="rounded-md bg-slate-50 border border-slate-200 p-3 mt-3 text-xs text-slate-600">
            <p className="flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5 text-green-600" />
              Need a human? Email{" "}
              <a
                href="mailto:support@credentiaai.com"
                className="text-blue-600 underline"
              >
                support@credentiaai.com
              </a>{" "}
              — we reply within 1 business day.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
